"use client";

import useEmblaCarousel from "embla-carousel-react";
import { FC, useCallback, useEffect, useState } from "react";

import { ColProfileCard } from "@/features/common";
import LeftIcon from "@/svgs/angle_left_b.svg";

import { LastNodeMessage } from "../../../last-node-message";

import styles from "./column-cards.module.scss";
import { ColumnCardsProps } from "./column-cards.types";

const ColumnCards: FC<ColumnCardsProps> = ({
  members,
  hasNextPage,
  isFetchingNextPage,
  withoutLastNodeMessage,
  currentIndex,
  markViewing = false,
  changeIndex,
  fetchNextPage,
}) => {
  const [verticalCarouselRef, verticalCarouselApi] = useEmblaCarousel({ axis: "y" });

  const [viewedUserIds, setViewedUserIds] = useState<string[]>([]);

  useEffect(() => {
    if (verticalCarouselApi) {
      verticalCarouselApi?.scrollTo(currentIndex, true);
    }
  }, [verticalCarouselApi]);

  const onScroll = useCallback(() => {
    if (!verticalCarouselApi) return;

    const selectedIndex = verticalCarouselApi.selectedScrollSnap();
    changeIndex(selectedIndex);

    if (selectedIndex === members.length - 5 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [verticalCarouselApi, members.length, fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (verticalCarouselApi) {
      verticalCarouselApi.on("select", onScroll);
    }
    return () => {
      if (verticalCarouselApi) verticalCarouselApi.off("select", onScroll);
    };
  }, [verticalCarouselApi, onScroll]);

  useEffect(() => {
    const currentUserId = members[currentIndex]?.id;

    if (markViewing && currentUserId) {
      const isUserFound = viewedUserIds.find((userId) => userId === currentUserId);

      if (!isUserFound) {
        setViewedUserIds([...viewedUserIds, currentUserId]);
      }
    }

    // while refetching not to see card with wrong index in vertical carousel
    if (currentIndex === 0) {
      verticalCarouselApi?.scrollTo(currentIndex);
    }
  }, [currentIndex]);

  return (
    <div className={styles.verticalContainer}>
      <div ref={verticalCarouselRef} className={styles.viewport}>
        <div className={styles.container}>
          {members.map((profileData) => (
            <div key={profileData.id} className={styles.slide}>
              <ColProfileCard profileRedirect {...profileData} />
            </div>
          ))}

          {!withoutLastNodeMessage && <LastNodeMessage />}
        </div>
      </div>

      <div className={styles.verticalScrollCarouselButtons}>
        <button disabled={!currentIndex} onClick={() => verticalCarouselApi?.scrollPrev()}>
          <LeftIcon className={styles.icon} />
        </button>

        <button onClick={() => verticalCarouselApi?.scrollNext()}>
          <LeftIcon className={styles.icon} />
        </button>
      </div>
    </div>
  );
};

export { ColumnCards };
